import { For, createSignal, onCleanup, onMount } from 'solid-js';
import {
  nextTacticPuzzle,
  pieceEndsOn,
  type TacticPuzzle,
} from '@domain/tactics/catalog';
import {
  TACTIC_KINDS,
  isTacticKind,
  tacticKindLabel,
  type TacticFilter,
} from '@domain/tactics/kinds';
import { recordModeAttempt } from '@state/modeStore';
import {
  loadTacticSolution,
  newGame,
  setOrientation,
  setRevealCorrectSquares,
} from '@state/actions';
import { gameStore } from '@state/gameStore';
import { GameControls } from '@ui/Controls/GameControls';
import './TacticAlertQuiz.css';

export function TacticAlertQuiz() {
  const [filter, setFilter] = createSignal<TacticFilter>('all');
  const [puzzle, setPuzzle] = createSignal<TacticPuzzle | null>(
    nextTacticPuzzle('all'),
  );
  const [revealed, setRevealed] = createSignal(false);

  function present(next: TacticPuzzle | null, nextFilter: TacticFilter = filter()) {
    setFilter(nextFilter);
    setPuzzle(next);
    setRevealed(false);
    setOrientation('white');
    if (next) newGame(next.fen);
    else newGame();
  }

  onMount(() => {
    present(puzzle());
  });

  onCleanup(() => {
    newGame();
  });

  function onReveal() {
    const p = puzzle();
    if (!p) return;
    const won =
      pieceEndsOn(p.from, gameStore.history.slice(0, gameStore.cursor)) === p.to;
    recordModeAttempt('tactic-alert', won);
    const loaded = loadTacticSolution({
      startFen: p.fen,
      from: p.from,
      to: p.to,
      name: tacticKindLabel(p.kind),
    });
    if (loaded && won) setRevealCorrectSquares([p.to]);
    setRevealed(true);
  }

  function onNext() {
    present(nextTacticPuzzle(filter(), { excludeFen: puzzle()?.fen }));
  }

  function resetQuiz() {
    const p = puzzle();
    setRevealed(false);
    setOrientation('white');
    if (p) newGame(p.fen);
    else newGame();
  }

  function onFilterChange(value: string) {
    const next: TacticFilter =
      value === 'all' || isTacticKind(value) ? value : 'all';
    present(nextTacticPuzzle(next), next);
  }

  const hasPuzzle = () => puzzle() !== null;

  return (
    <>
      <section class="game-rules" aria-label="Game rules">
        <p>
          Play the one move that creates a tactic, then reveal the solution.
        </p>
      </section>
      <GameControls onReset={resetQuiz} />
      <div class="tactic-alert__toolbar">
        <select
          class="tactic-alert__select"
          aria-label="Tactic type"
          value={filter()}
          onChange={(e) => onFilterChange(e.currentTarget.value)}
        >
          <option value="all">All</option>
          <For each={TACTIC_KINDS}>
            {(k) => (
              <option value={k.id}>{k.label}</option>
            )}
          </For>
        </select>
        <button
          type="button"
          class="btn tactic-alert__reveal"
          disabled={!hasPuzzle()}
          onClick={() => (revealed() ? onNext() : onReveal())}
        >
          {revealed() ? 'Next' : 'Reveal'}
        </button>
      </div>
    </>
  );
}
